import { debounce } from 'lodash';
import { createSignal } from 'solid-js';

export function useDebounceRequest<T, P>(
  requestFn: (params: P) => Promise<T | null>,
  debounceTime = 300,
) {
  const [isLoading, setIsLoading] = createSignal(false);
  const [data, setData] = createSignal<T | null>(null);
  const [error, setError] = createSignal<Error | null>(null);

  // Monotonically-increasing token: each new execute call gets a higher token.
  // When a response arrives, it is only applied if its token still matches the
  // latest one, which discards any in-flight responses from earlier queries.
  let requestToken = 0;

  const execute = debounce(async (params: P): Promise<T | null> => {
    const token = ++requestToken;

    setIsLoading(true);
    setError(null);

    try {
      const result = await requestFn(params);

      // Discard stale responses (user has already typed something newer).
      if (token !== requestToken) return null;

      setData(() => result);
    } catch (err) {
      if (token !== requestToken) return null;

      setError(err as Error);
      setData(null);
    } finally {
      if (token === requestToken) {
        setIsLoading(false);
      }
    }
    return data();
  }, debounceTime);

  return {
    execute,
    isLoading,
    data,
    setData,
    error,
  };
}
